import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const SuccessCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 40px;
  max-width: 500px;
  width: 100%;
  text-align: center;
  color: #2c3e50;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const SuccessIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  filter: drop-shadow(0 4px 8px rgba(76, 175, 80, 0.3));
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 15px;
  background: linear-gradient(45deg, #4caf50, #45b7d1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #7f8c8d;
  margin-bottom: 25px;
  line-height: 1.5;
`;

const PurchaseDetails = styled.div`
  background: rgba(255, 255, 255, 0.5);
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  text-align: left;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  
  &.total {
    font-weight: bold;
    font-size: 1.1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding-top: 10px;
    margin-top: 15px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 30px;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Button = styled(motion.button)`
  flex: 1;
  padding: 15px 25px;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &.primary {
    background: linear-gradient(45deg, #4ecdc4, #45b7d1);
    color: white;
    
    &:hover {
      box-shadow: 0 8px 25px rgba(78, 205, 196, 0.3);
    }
  }
  
  &.secondary {
    background: transparent;
    color: #4ecdc4;
    border: 2px solid #4ecdc4;
    
    &:hover {
      background: rgba(78, 205, 196, 0.1);
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #e0e0e0;
  border-top: 3px solid #4ecdc4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 20px auto;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const { clearCart } = useCart();
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (sessionId) {
      // In a real app, you'd verify the session with your backend
      // For now, we'll simulate the order details
      setTimeout(() => {
        setOrderDetails({
          sessionId,
          amount: '$4.99',
          subscription: 'Premium Monthly',
          date: new Date().toLocaleDateString(),
        });
        setLoading(false);
        
        // Clear the cart and refresh user data
        clearCart();
        if (refreshUser) {
          refreshUser();
        }
      }, 2000);
    } else {
      setLoading(false);
    }
  }, [searchParams, clearCart, refreshUser]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleViewDashboard = () => {
    // Navigate to user dashboard/profile
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <Container>
        <SuccessCard
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <LoadingSpinner />
          <Title>Processing Payment...</Title>
          <Subtitle>Please wait while we confirm your payment.</Subtitle>
        </SuccessCard>
      </Container>
    );
  }

  return (
    <Container>
      <SuccessCard
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <SuccessIcon>🎉</SuccessIcon>
        
        <Title>Payment Successful!</Title>
        
        <Subtitle>
          Thank you for your purchase! You now have access to all premium features.
          Your premium benefits are active immediately.
        </Subtitle>

        {orderDetails && (
          <PurchaseDetails>
            <DetailRow>
              <span>Order ID:</span>
              <span>{orderDetails.sessionId.slice(-8).toUpperCase()}</span>
            </DetailRow>
            <DetailRow>
              <span>Subscription:</span>
              <span>{orderDetails.subscription}</span>
            </DetailRow>
            <DetailRow>
              <span>Date:</span>
              <span>{orderDetails.date}</span>
            </DetailRow>
            <DetailRow className="total">
              <span>Amount Paid:</span>
              <span>{orderDetails.amount}</span>
            </DetailRow>
          </PurchaseDetails>
        )}

        <div style={{
          background: 'rgba(76, 175, 80, 0.1)',
          border: '1px solid rgba(76, 175, 80, 0.3)',
          borderRadius: '8px',
          padding: '15px',
          margin: '20px 0',
          fontSize: '0.9rem',
          color: '#2e7d32'
        }}>
          ✅ Premium features unlocked<br/>
          ✅ Unlimited wheel segments<br/>
          ✅ Custom themes available<br/>
          ✅ Private rooms enabled
        </div>

        <ActionButtons>
          <Button
            className="primary"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleGoHome}
          >
            Start Playing
          </Button>
          
          <Button
            className="secondary"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleViewDashboard}
          >
            View Dashboard
          </Button>
        </ActionButtons>
      </SuccessCard>
    </Container>
  );
};

export default PaymentSuccess; 